import {
  MAP_ROTATION_ANGLE, POSITION_HISTORY_SIZE, MOVEMENT_THRESHOLD,
  TERMINAL_THRESHOLD, ROUTE_PROXIMITY_THRESHOLD, CONSTANTS,
  ROUTE_INDEX_CHANGE_THRESHOLD,
} from './mapOptions'
export const calculateRealisticETA = (distance) => {
  const distanceInKm = distance / 1000;
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;
  const numTrafficLights = Math.floor(distance / 800);
  const avgTrafficLightDelay = 20;
  baseTime += numTrafficLights * avgTrafficLightDelay;
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME
  );
};

// Existing calculateImprovedDistance (keep this as it's used elsewhere)
export const calculateImprovedDistance = (pos1, pos2) => {
  if (
    !pos1?.lat ||
    !pos1?.lng ||
    !pos2?.lat ||
    !pos2?.lng ||
    isNaN(pos1.lat) ||
    isNaN(pos1.lng) ||
    isNaN(pos2.lat) ||
    isNaN(pos2.lng)
  ) {
    console.warn("Invalid coordinates for distance calculation:", { pos1, pos2 });
    return null;
  }
  try {
    const φ1 = (parseFloat(pos1.lat) * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.lat) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.lat) - parseFloat(pos1.lat)) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.lng) - parseFloat(pos1.lng)) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = (CONSTANTS.EARTH_RADIUS || 6371000) * c;
    const minDistance = CONSTANTS.MIN_STOP_DISTANCE || 10;
    const maxDistance = CONSTANTS.MAX_STOP_DISTANCE || 100000;
    return Math.min(Math.max(distance, minDistance), maxDistance);
  } catch (error) {
    console.error("Error calculating distance:", error);
    return null;
  }
};
export const formatTime = (timeString) => {
  if (!timeString) return "---";
  if (
    typeof timeString === "string" &&
    (timeString.includes("m") || timeString.includes("s"))
  ) {
    return timeString;
  }
  const seconds =
    typeof timeString === "string" ? parseInt(timeString) : timeString;
  if (isNaN(seconds) || seconds <= 0) return "---";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

// New: calculateBearing function (this was missing but used)
export const calculateBearing = (p1, p2) => {
  if (
    !p1 ||
    !p2 ||
    isNaN(p1.latitude) ||
    isNaN(p1.longitude) ||
    isNaN(p2.latitude) ||
    isNaN(p2.longitude)
  ) {
    console.error("Invalid coordinates for bearing calculation:", p1, p2);
    return 0;
  }
  if (window.google && window.google.maps.geometry) {
    const from = new window.google.maps.LatLng(p1.latitude, p1.longitude);
    const to = new window.google.maps.LatLng(p2.latitude, p2.longitude);
    const bearing = window.google.maps.geometry.spherical.computeHeading(from, to);
    return (bearing + 360) % 360; // Normalize to 0-360
  }
  // Fallback to manual calculation
  const lat1 = (p1.latitude * Math.PI) / 180;
  const lon1 = (p1.longitude * Math.PI) / 180;
  const lat2 = (p2.latitude * Math.PI) / 180;
  const lon2 = (p2.longitude * Math.PI) / 180;
  const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

// Existing calculateDistance (Haversine formula)
export const calculateDistance = (p1, p2) => {
  if (!p1 || !p2 || isNaN(p1.latitude) || isNaN(p1.longitude) || isNaN(p2.latitude) || isNaN(p2.longitude)) return Infinity;
  const R = 6371e3; // metres
  const φ1 = (p1.latitude * Math.PI) / 180; // φ, λ in radians
  const φ2 = (p2.latitude * Math.PI) / 180;
  const Δφ = ((p2.latitude - p1.latitude) * Math.PI) / 180;
  const Δλ = ((p2.longitude - p1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in meters
};

// New: Improved getOverallRouteBearing
export const getOverallRouteBearing = (routeCoordinates) => {
  if (!routeCoordinates || routeCoordinates.length < 2) return 0;

  let totalBearing = 0;
  let count = 0;
  for (let i = 0; i < routeCoordinates.length - 1; i++) {
    const p1 = {
      latitude: parseFloat(routeCoordinates[i].latitude),
      longitude: parseFloat(routeCoordinates[i].longitude),
    };
    const p2 = {
      latitude: parseFloat(routeCoordinates[i + 1].latitude),
      longitude: parseFloat(routeCoordinates[i + 1].longitude),
    };

    // Only calculate bearing for segments that are not extremely short
    // or if points are identical (to avoid NaN from atan2)
    const distance = calculateDistance(p1, p2);
    if (distance > 5) { // Only consider segments longer than 5 meters for bearing
      const bearing = calculateBearing(p1, p2);
      if (!isNaN(bearing)) { // Ensure bearing is a valid number
        totalBearing += bearing;
        count++;
      }
    }
  }
  return count > 0 ? totalBearing / count : 0;
};

export const normalizeDeviceData = (data) => {
  if (!data) return null;

  const parsedSpeed = parseFloat(data.speed ?? data.Speed);
  const speed = isNaN(parsedSpeed) ? 0 : parsedSpeed;

  const parsedBearing = parseFloat(data.bearing ?? data.heading ?? data.course);
  const bearing = isNaN(parsedBearing) ? null : parsedBearing;

  const deviceId = data.deviceId || data.device_id;
  const latitude = parseFloat(data.latitude);
  const longitude = parseFloat(data.longitude);

  return {
    ...data,
    deviceId,
    device_id: deviceId,
    latitude: isNaN(latitude) ? 0 : latitude,
    longitude: isNaN(longitude) ? 0 : longitude,
    speed,
    Speed: speed,
    bearing,
    heading: bearing,
    timestamp: data.timestamp || new Date().toISOString(),
    rawData: data.RawData || "",
  };
};


export const detectMovement = (newPos, lastPos) => {
  if (!lastPos || !newPos) return true;
  const distance = calculateDistance(
    { latitude: newPos.latitude, longitude: newPos.longitude },
    { latitude: lastPos.latitude, longitude: lastPos.longitude }
  );
  console.log(`[${newPos.deviceId}] Movement check: ${distance} meters`);
  return distance > MOVEMENT_THRESHOLD;
};

export const determineVehicleDirection = (
  deviceId,
  currentPosition,
  stops,
  routes,
  vehicles,
  positionHistory = []
) => {
  const storageKey = `${deviceId}_driver`;
  const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
  let savedPositionHistory = storedData.positions || [];
  let lastMovedPosition = storedData.lastMovedPosition || null;
  let pathDirection = storedData.direction || "unknown";
  let lastRouteIndex = storedData.lastRouteIndex ?? null;
  let lastHeading = storedData.lastHeading ?? null;

  const telemetrySpeed = parseFloat(
    currentPosition?.speed ?? currentPosition?.Speed
  );
  let speed = !isNaN(telemetrySpeed) && telemetrySpeed > 0 ? telemetrySpeed : 0;

  // Validate stored data
  if (
    savedPositionHistory.length &&
    savedPositionHistory.some(
      (pos) => !pos.latitude || !pos.longitude || !pos.timestamp
    )
  ) {
    console.warn(`[${deviceId}] Clearing invalid stored position history`);
    savedPositionHistory = [];
    lastMovedPosition = null;
    pathDirection = "unknown";
  }

  const isMoving = detectMovement(currentPosition, lastMovedPosition);
  if (isMoving) {
    savedPositionHistory.push({
      ...currentPosition,
      timestamp: new Date().toISOString(),
    });
    lastMovedPosition = currentPosition;
    if (savedPositionHistory.length > POSITION_HISTORY_SIZE) {
      savedPositionHistory = savedPositionHistory.slice(-POSITION_HISTORY_SIZE);
    }
  }

  const saveToStorage = () => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        positions: savedPositionHistory,
        lastMovedPosition,
        direction: pathDirection,
        lastRouteIndex,
        lastHeading,
        timestamp: Date.now(),
        deviceId,
      })
    );
  };

  if (!stops || stops.length < 2 || !routes || routes.length === 0) {
    console.warn(
      `[${deviceId}] Invalid data: stops=${stops?.length}, routes=${routes?.length}`
    );
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const vehicle = vehicles.find((v) => v.device_id === deviceId);
  if (!vehicle || !vehicle.routeId) {
    console.warn(`[${deviceId}] No vehicle or routeId found`);
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const vehicleRoute = routes.find((r) => r.id === vehicle.routeId);
  if (
    !vehicleRoute ||
    !vehicleRoute.routeCoordinates ||
    vehicleRoute.routeCoordinates.length === 0
  ) {
    console.warn(`[${deviceId}] No valid route found`);
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const routeCoords = vehicleRoute.routeCoordinates;

  const findClosestIndex = (position) => {
    let minDistance = Infinity;
    let closestIndex = -1;
    routeCoords.forEach((coord, index) => {
      const d = calculateDistance(
        { latitude: parseFloat(coord.latitude), longitude: parseFloat(coord.longitude) },
        { latitude: position.latitude, longitude: position.longitude }
      );
      if (d < minDistance) {
        minDistance = d;
        closestIndex = index;
      }
    });
    return { index: closestIndex, distance: minDistance };
  };

  const { index: currentRouteIndex, distance: minDistanceToRoute } =
    findClosestIndex(currentPosition);

  if (minDistanceToRoute >= ROUTE_PROXIMITY_THRESHOLD) {
    console.warn(`[${deviceId}] Vehicle not on route: min distance > ${ROUTE_PROXIMITY_THRESHOLD}m`);
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const source = {
    latitude: parseFloat(stops[0].latitude),
    longitude: parseFloat(stops[0].longitude),
  };
  const destination = {
    latitude: parseFloat(stops[stops.length - 1].latitude),
    longitude: parseFloat(stops[stops.length - 1].longitude),
  };
  const isRoundTrip = calculateDistance(source, destination) < TERMINAL_THRESHOLD;

  if (currentRouteIndex !== -1 && lastRouteIndex !== null) {
    const indexDelta = currentRouteIndex - lastRouteIndex;
    if (Math.abs(indexDelta) >= ROUTE_INDEX_CHANGE_THRESHOLD) {
      pathDirection = indexDelta > 0 ? "source_to_dest" : "dest_to_source";
    }
  }
  if (currentRouteIndex !== -1) {
    lastRouteIndex = currentRouteIndex;
  }

  const nearSource = calculateDistance(currentPosition, source) < TERMINAL_THRESHOLD;
  const nearDest = calculateDistance(currentPosition, destination) < TERMINAL_THRESHOLD;

  if (isRoundTrip && (nearSource || nearDest)) {
    pathDirection = nearSource ? "source_to_dest" : "dest_to_source";
  } else if (nearSource && pathDirection !== "source_to_dest") {
    pathDirection = "source_to_dest";
  } else if (nearDest && pathDirection !== "dest_to_source") {
    pathDirection = "dest_to_source";
  }

  // Determine local route segment bearing at current vehicle position
  let localRouteBearing = 0;
  if (routeCoords && routeCoords.length >= 2) {
    const idx = currentRouteIndex !== -1 ? currentRouteIndex : 0;
    let p1 = routeCoords[idx];
    let p2 = routeCoords[Math.min(idx + 1, routeCoords.length - 1)];
    if (idx === routeCoords.length - 1 && routeCoords.length > 1) {
      p1 = routeCoords[idx - 1];
      p2 = routeCoords[idx];
    }
    if (p1 && p2 && (p1.latitude !== p2.latitude || p1.longitude !== p2.longitude)) {
      localRouteBearing = calculateBearing(
        { latitude: parseFloat(p1.latitude), longitude: parseFloat(p1.longitude) },
        { latitude: parseFloat(p2.latitude), longitude: parseFloat(p2.longitude) }
      );
    }
  }

  // Expected travel bearing along local route segment for current pathDirection
  const expectedTravelBearing =
    pathDirection === "dest_to_source"
      ? (localRouteBearing + 180) % 360
      : localRouteBearing;

  const telemetryBearing = parseFloat(
    currentPosition?.bearing ?? currentPosition?.heading ?? currentPosition?.Heading ?? currentPosition?.Course
  );
  let candidateBearing = !isNaN(telemetryBearing) && telemetryBearing > 0 ? telemetryBearing : 0;

  if (candidateBearing === 0 && savedPositionHistory.length >= 2) {
    const prevPos = savedPositionHistory[savedPositionHistory.length - 2];
    const moveDist = calculateDistance(prevPos, currentPosition);
    if (moveDist >= MOVEMENT_THRESHOLD) {
      candidateBearing = calculateBearing(prevPos, currentPosition);
    }
  }

  if (candidateBearing > 0) {
    // Ensure candidate bearing aligns with expected travel direction along the local road segment
    const diff = Math.abs(((candidateBearing - expectedTravelBearing + 180) % 360) - 180);
    if (diff > 90) {
      lastHeading = (candidateBearing + 180) % 360;
    } else {
      lastHeading = candidateBearing;
    }
  } else if (typeof lastHeading !== "number" || isNaN(lastHeading) || lastHeading === 0) {
    lastHeading = expectedTravelBearing;
  }

  if ((typeof lastHeading !== "number" || isNaN(lastHeading) || lastHeading === 0) && stops && stops.length >= 2) {
    const stop0 = { latitude: parseFloat(stops[0].latitude), longitude: parseFloat(stops[0].longitude) };
    const stop1 = { latitude: parseFloat(stops[1].latitude), longitude: parseFloat(stops[1].longitude) };
    if (!isNaN(stop0.latitude) && !isNaN(stop1.latitude)) {
      let stopBearing = calculateBearing(stop0, stop1);
      if (pathDirection === "dest_to_source") {
        stopBearing = (stopBearing + 180) % 360;
      }
      lastHeading = stopBearing;
    }
  }

  const finalBearing = typeof lastHeading === "number" && !isNaN(lastHeading) ? lastHeading : expectedTravelBearing;

  saveToStorage();
  return { direction: pathDirection, speed, bearing: finalBearing };
};

export const calculateLabelOffsets = (positions) => {
  const offsets = new Array(positions.length).fill(0);
  const labelHeight = 30; // Approximate height of a label in pixels

  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const pos1 = positions[i];
      const pos2 = positions[j];
      const distance = calculateImprovedDistance(pos1, pos2);
      if (distance < 0.001) {
        // Adjust threshold based on your map scale
        offsets[j] += labelHeight; // Stack vertically
      }
    }
  }

  return offsets;
};

export const calculateMapRotation = (source, destination) => {
  if (!source || !destination) return 0;

  try {
    const lat1 = parseFloat(source.latitude);
    const lng1 = parseFloat(source.longitude);
    const lat2 = parseFloat(destination.latitude);
    const lng2 = parseFloat(destination.longitude);

    if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) {
      console.warn('Invalid coordinates for rotation:', { source, destination });
      return 0;
    }

    // Calculate angle in degrees
    const dy = lat2 - lat1;
    const dx = lng2 - lng1;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Adjust angle so that source->destination points upward (0 degrees)
    angle = 90 - angle;

    // Normalize angle to 0-360
    angle = (angle + 360) % 360;

    return angle;
  } catch (error) {
    console.error('Error calculating map rotation:', error);
    return 0;
  }
};